import React from 'react';
import { Box, Container, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaTicketAlt,
  FaUsers,
} from 'react-icons/fa';
import { getEventLocation } from './eventLocations';

const InfoCell = ({
  icon: IconComponent,
  label,
  title,
  subtitle,
  color,
  isLast,
}) => (
  <Flex
    gap={3}
    p={4}
    minW={0}
    borderRight={{ base: 0, md: isLast ? 0 : '1px solid' }}
    borderBottom={{ base: isLast ? 0 : '1px solid', md: 0 }}
    borderColor="gray.200"
  >
    <Flex
      flex="none"
      w={9}
      h={9}
      borderRadius="md"
      bg={`${color}.50`}
      align="center"
      justify="center"
      color={`${color}.500`}
    >
      <IconComponent />
    </Flex>
    <Box minW={0}>
      <Text
        fontSize="xs"
        letterSpacing="wider"
        textTransform="uppercase"
        color="gray.500"
      >
        {label}
      </Text>
      <Text fontWeight="semibold" lineHeight="short" color="gray.800">
        {title}
      </Text>
      {subtitle ? (
        <Text fontSize="sm" color="gray.600">
          {subtitle}
        </Text>
      ) : null}
    </Box>
  </Flex>
);

const formatHHMM = (time) =>
  time ? time.split(':').slice(0, 2).join(':') : null;

/**
 * Barra info rapida sotto l'hero: quando, dove, per chi, prezzo.
 * `whenTitle` e `priceTitle` sono calcolati da EventDetail (ricorrenze, varianti, sconti).
 */
const EventInfoBar = ({ event, whenTitle, priceTitle, priceSubtitle }) => {
  const start = formatHHMM(event?.Ora_inizio);
  const end = formatHHMM(event?.Ora_fine);
  const hours = start && end ? `${start} – ${end}` : start;

  const location = getEventLocation(event?.Location);
  const locationSubtitle = location?.address
    ? location.address.split(',').slice(-1)[0].trim()
    : null;

  const registrationRule =
    event?.tipoIscrizione === 'solo_soci'
      ? 'Riservato ai soci'
      : event?.tipoIscrizione === 'aperto_tutti'
        ? 'Aperto a tutti'
        : null;

  const cells = [
    {
      icon: FaCalendarAlt,
      label: 'Quando',
      color: 'blue',
      title: whenTitle,
      subtitle: hours,
    },
    location && {
      icon: FaMapMarkerAlt,
      label: 'Dove',
      color: 'red',
      title: location.title,
      subtitle: locationSubtitle || location.subtitle,
    },
    (event?.Partecipanti || registrationRule) && {
      icon: FaUsers,
      label: 'Per chi',
      color: 'pink',
      title: event?.Partecipanti || registrationRule,
      subtitle: event?.Partecipanti ? registrationRule : null,
    },
    priceTitle && {
      icon: FaTicketAlt,
      label: 'Prezzo',
      color: 'green',
      title: priceTitle,
      subtitle: priceSubtitle,
    },
  ].filter(Boolean);

  return (
    <Container maxW="container.xl" px={{ base: 2, md: 4 }}>
      <SimpleGrid
        columns={{ base: 1, md: cells.length }}
        bg="white"
        borderRadius="lg"
        boxShadow="xl"
        mt={{ base: -10, md: -12 }}
        mb={6}
        position="relative"
        zIndex={3}
      >
        {cells.map((cell, i) => (
          <InfoCell
            key={cell.label}
            {...cell}
            isLast={i === cells.length - 1}
          />
        ))}
      </SimpleGrid>
    </Container>
  );
};

export default EventInfoBar;
